import BlockRevealRegion from "./BlockRevealRegion";
import BlockDragHandle, {
  BlockBodyAction,
  BlockBodyActions,
  BlockCardHeader,
  BlockThumbnail,
  DuplicatePath,
  TrashPath,
} from "./blockChrome";
import {
  isPlannedExerciseNoteTooLong,
  PLANNED_EXERCISE_NOTE_MAX_LENGTH,
  PLANNED_EXERCISE_NOTE_TOO_LONG_COPY,
} from "./plannedExerciseNote";

// Tier-3 builder presentation seam (Phase 7A) -- Design V2 cardio block.
//
// Cardio keeps its own prescription vocabulary: duration, modality-derived
// machine settings with used-key filtering, and heart-rate mode plus value.
// Strength set-row semantics are deliberately NOT imposed on it. Every option
// list, filter and clamp arrives from the container.
export default function BlockEditorCardio({
  block,
  blockUiKey,
  exerciseName,
  prescription,
  heartRateMode,
  heartRateModeOptions,
  heartRateValue,
  machineSettingOptionsFor,
  machineSettingLabelFor,
  usedMachineSettingKeys,
  canAddMachineSetting,
  isCollapsed,
  isDragging,
  disabled = false,
  sortable,
  onToggle,
  onDelete,
  onDuplicate,
  onDurationChange,
  onHeartRateModeChange,
  onHeartRateValueChange,
  onStepHeartRate,
  onAddMachineSetting,
  onUpdateMachineSetting,
  onRemoveMachineSetting,
  onNotesChange,
}) {
  const durationId = `lz-cardio-duration-${blockUiKey}`;
  // Summary reads only what the cardio prescription already carries.
  const durationSummary =
    prescription.durationMinutes === "" || prescription.durationMinutes == null
      ? ""
      : `${prescription.durationMinutes} min`;

  return (
    <section
      ref={sortable.setNodeRef}
      style={sortable.style}
      data-block-id={blockUiKey}
      data-dragging={isDragging ? "true" : undefined}
      className="lz-builder-block lz-builder-block--cardio"
    >
      <BlockCardHeader
        isCollapsed={isCollapsed}
        toggleLabel={isCollapsed ? "Expand block" : "Collapse block"}
        onToggle={onToggle}
        dragHandle={
          <BlockDragHandle
            disabled={disabled}
            setHandleRef={sortable.setHandleRef}
            handleAttributes={sortable.handleAttributes}
            handleListeners={sortable.handleListeners}
          />
        }
        thumbnail={
          <BlockThumbnail
            exerciseId={block.exerciseId || block.exercise?.exerciseId}
          />
        }
        eyebrow="Cardio"
        title={exerciseName}
        meta={
          durationSummary ? (
            <p className="lz-builder-block__meta">{durationSummary}</p>
          ) : null
        }
      />

      <BlockRevealRegion open={!isCollapsed}>
        {() => (
        <div className="lz-builder-block__body">
          <div className="lz-builder-block__controls">
            <div className="lz-builder-field-group">
              <label className="lz-builder-field-label" htmlFor={durationId}>
                Duration
              </label>
              <div className="lz-builder-duration">
                <input
                  id={durationId}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  disabled={disabled}
                  value={prescription.durationMinutes}
                  onChange={(event) => onDurationChange(event.target.value)}
                  className="lz-builder-field"
                />
                <span className="lz-builder-duration__unit">min</span>
              </div>
            </div>

            <div className="lz-builder-field-group">
              <span className="lz-builder-field-label" id={`${blockUiKey}-hr-mode`}>
                HR Target
              </span>
              <div
                className="lz-builder-segment"
                role="radiogroup"
                aria-labelledby={`${blockUiKey}-hr-mode`}
              >
                {heartRateModeOptions.map((option) => {
                  const isSelected = heartRateMode === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={disabled}
                      onClick={() => onHeartRateModeChange(option.value)}
                      className="lz-builder-segment__option"
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {heartRateMode === "none" ? null : (
            <div className="lz-builder-field-group">
              <span className="lz-builder-field-label">
                {heartRateMode === "zone" ? "Zone" : "Avg BPM"}
              </span>
              <div className="lz-builder-stepper lz-builder-stepper--wide">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onStepHeartRate(-1)}
                  className="lz-builder-stepper__button"
                  aria-label={heartRateMode === "zone" ? "Decrease zone" : "Decrease BPM"}
                >
                  <span aria-hidden="true">&minus;</span>
                </button>

                {heartRateMode === "zone" ? (
                  <span className="lz-builder-stepper__value">{heartRateValue}</span>
                ) : (
                  <input
                    type="number"
                    inputMode="numeric"
                    min="80"
                    max="200"
                    step="1"
                    disabled={disabled}
                    value={prescription.heartRateTargetValue || 140}
                    onChange={(event) => onHeartRateValueChange(event.target.value)}
                    aria-label="Average BPM target"
                    className="lz-builder-stepper__input"
                  />
                )}

                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onStepHeartRate(1)}
                  className="lz-builder-stepper__button"
                  aria-label={heartRateMode === "zone" ? "Increase zone" : "Increase BPM"}
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
            </div>
          )}

          <div className="lz-builder-machine">
            <div className="lz-builder-machine__header">
              <span className="lz-builder-field-label">Machine settings</span>
              {canAddMachineSetting ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={onAddMachineSetting}
                  className="lz-builder-machine__add"
                >
                  <span aria-hidden="true">+</span>
                  <span>Add setting</span>
                </button>
              ) : null}
            </div>

            {prescription.machineSettings.length > 0 ? (
              <div className="lz-builder-machine__rows">
                {prescription.machineSettings.map((setting, settingIndex) => {
                  const options = machineSettingOptionsFor(setting.key);

                  return (
                    <div
                      className="lz-builder-machine__row"
                      key={`${block.id}-machine-${settingIndex}`}
                    >
                      {options.length <= 1 ? (
                        <span className="lz-builder-machine__static">
                          {machineSettingLabelFor(setting.key)}
                        </span>
                      ) : (
                        <div className="lz-builder-segment lz-builder-segment--compact">
                          {options.map((option) => {
                            const isSelected = option.value === setting.key;
                            const isUnavailable =
                              option.value !== setting.key &&
                              usedMachineSettingKeys.has(option.value);

                            return (
                              <button
                                key={option.value}
                                type="button"
                                aria-pressed={isSelected}
                                disabled={disabled || isUnavailable}
                                onClick={() =>
                                  onUpdateMachineSetting(settingIndex, {
                                    key: option.value,
                                  })
                                }
                                className="lz-builder-segment__option"
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <input
                        type="text"
                        disabled={disabled}
                        value={setting.value}
                        onChange={(event) =>
                          onUpdateMachineSetting(settingIndex, {
                            value: event.target.value,
                          })
                        }
                        placeholder="Value"
                        aria-label={`${machineSettingLabelFor(setting.key)} value`}
                        className="lz-builder-field"
                      />

                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onRemoveMachineSetting(settingIndex)}
                        className="lz-builder-machine__remove"
                        aria-label="Remove machine setting"
                      >
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <textarea
            disabled={disabled}
            value={prescription.notes}
            maxLength={PLANNED_EXERCISE_NOTE_MAX_LENGTH}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Cardio notes..."
            aria-label="Cardio notes"
            className="lz-builder-notes"
          />
          {isPlannedExerciseNoteTooLong(prescription.notes) ? (
            <p className="lz-builder-block__hint" role="status">{PLANNED_EXERCISE_NOTE_TOO_LONG_COPY}</p>
          ) : null}

          {/* Same delete callback as before, moved off the collapsed card. */}
          <BlockBodyActions
            trailing={
              <BlockBodyAction
                label="Delete Cardio"
                tone="danger"
                iconOnly
                disabled={disabled}
                onClick={onDelete}
              >
                <TrashPath />
              </BlockBodyAction>
            }
          >
            <BlockBodyAction
              label="Duplicate Block"
              disabled={disabled}
              onClick={onDuplicate}
            >
              <DuplicatePath />
            </BlockBodyAction>
          </BlockBodyActions>
        </div>
        )}
      </BlockRevealRegion>
    </section>
  );
}
